import { derived, writable } from "svelte/store";
import { identity, throttle, ensurePlural, getJson, setJson, batch, partition, first, } from "@welshman/lib";
import { matchFilters, getIdAndAddress, getIdFilters } from "@welshman/util";
// Sync with localstorage
export const synced = (key, defaultValue) => {
    const init = getJson(key);
    const store = writable(init === undefined ? defaultValue : init);
    store.subscribe((value) => setJson(key, value));
    return store;
};
// Getters
export const getter = (store) => {
    let value;
    store.subscribe((newValue) => {
        value = newValue;
    });
    return () => value;
};
export function withGetter(store) {
    return { ...store, get: getter(store) };
}
// Throttle
export const throttled = (delay, store) => {
    if (delay) {
        const { subscribe } = store;
        store = { ...store, subscribe: (f) => subscribe(throttle(delay, f)) };
    }
    return store;
};
export const custom = (start, opts = {}) => {
    const subs = [];
    let value;
    let stop;
    const set = (newValue) => {
        for (const sub of subs) {
            sub(newValue);
        }
        value = newValue;
    };
    return {
        get: () => value,
        set: (newValue) => {
            set(newValue);
            opts.set?.(newValue);
        },
        update: (f) => {
            const newValue = f(value);
            set(newValue);
            opts.set?.(newValue);
        },
        subscribe: (sub) => {
            if (opts.throttle) {
                sub = throttle(opts.throttle, sub);
            }
            if (subs.length === 0) {
                stop = start(set);
            }
            subs.push(sub);
            sub(value);
            return () => {
                subs.splice(subs.findIndex(s => s === sub), 1);
                if (subs.length === 0) {
                    stop();
                }
            };
        },
    };
};
// Simple adapter
export const adapter = ({ store, forward, backward, }) => ({
    ...derived(store, forward),
    set: (x) => store.set(backward(x)),
    update: (f) => store.update((x) => backward(f(forward(x)))),
});
export const deriveEventsMapped = (repository, { filters, eventToItem, itemToEvent, throttle = 0, includeDeleted = false, }) => custom(setter => {
    let data = [];
    const deferred = new Set();
    const defer = (event, promise) => {
        deferred.add(event.id);
        void promise.then(items => {
            if (deferred.has(event.id)) {
                deferred.delete(event.id);
                for (const item of ensurePlural(items)) {
                    data.push(item);
                }
                setter(data);
            }
        });
    };
    for (const event of repository.query(filters, { includeDeleted })) {
        const items = eventToItem(event);
        if (!items) {
            continue;
        }
        if (items instanceof Promise) {
            defer(event, items);
        }
        else {
            for (const item of ensurePlural(items)) {
                data.push(item);
            }
        }
    }
    setter(data);
    const onUpdate = batch(300, (updates) => {
        const removed = new Set();
        const added = new Map();
        // Apply updates in order
        for (const update of updates) {
            for (const event of update.added.values()) {
                added.set(event.id, event);
                removed.delete(event.id);
            }
            for (const id of update.removed) {
                removed.add(id);
                added.delete(id);
                deferred.delete(id);
            }
        }
        let dirty = false;
        for (const event of added.values()) {
            if (matchFilters(filters, event)) {
                const items = eventToItem(event);
                if (items instanceof Promise) {
                    defer(event, items);
                }
                else if (items) {
                    dirty = true;
                    for (const item of ensurePlural(items)) {
                        data.push(item);
                    }
                }
            }
        }
        if (!includeDeleted && removed.size > 0) {
            const [deleted, ok] = partition((item) => getIdAndAddress(itemToEvent(item)).some((id) => removed.has(id)), data);
            if (deleted.length > 0) {
                dirty = true;
                data = ok;
            }
        }
        if (dirty) {
            setter(data);
        }
    });
    repository.on("update", onUpdate);
    return () => repository.off("update", onUpdate);
}, { throttle });
export const deriveEvents = (repository, opts) => deriveEventsMapped(repository, {
    ...opts,
    eventToItem: identity,
    itemToEvent: identity,
});
export const deriveEvent = (repository, idOrAddress) => derived(deriveEvents(repository, {
    filters: getIdFilters([idOrAddress]),
    includeDeleted: true,
}), first);
export const deriveIsDeleted = (repository, event) => custom(setter => {
    setter(repository.isDeleted(event));
    const onUpdate = batch(300, () => setter(repository.isDeleted(event)));
    repository.on("update", onUpdate);
    return () => repository.off("update", onUpdate);
});
export const deriveIsDeletedByAddress = (repository, event) => custom(setter => {
    setter(repository.isDeletedByAddress(event));
    const onUpdate = batch(300, () => setter(repository.isDeletedByAddress(event)));
    repository.on("update", onUpdate);
    return () => repository.off("update", onUpdate);
});
//# sourceMappingURL=index.js.map